import { forwardRef, type HTMLAttributes, type ReactElement, type ReactNode, type Ref } from 'react'
import { Accordion as BaseAccordion } from '@base-ui/react/accordion'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import './accordion.css'

export type AccordionHeadingLevel = 2 | 3 | 4 | 5 | 6

export type AccordionItem<Value extends string> = {
  /** Identifies the section in `value`, `defaultValue` and `onValueChange`. */
  value: Value
  /** The section’s heading. It names the trigger, so keep it short, for example a question. */
  title: ReactNode
  /** The content revealed when the section opens. */
  content: ReactNode
  disabled?: boolean
}

export type AccordionProps<Value extends string> = Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'children'> & {
  /** Names the set of sections for assistive technology when no visible heading does, for example “Camera questions”. */
  label?: string
  items: AccordionItem<Value>[]
  /** Let several sections stay open together. By default, opening one closes the others. */
  multiple?: boolean
  /** The open sections, when controlled. Always an array, even when one section opens at a time. */
  value?: Value[]
  /** The sections open at first, when uncontrolled. */
  defaultValue?: Value[]
  onValueChange?: (value: Value[]) => void
  /** The heading level that wraps each trigger, so the page outline stays meaningful. */
  headingLevel?: AccordionHeadingLevel
  /** Make every section unavailable, for example while the content loads. */
  disabled?: boolean
  /** Keep closed sections in the DOM, for example to preserve form input. */
  keepMounted?: boolean
  /** Let the browser’s find in page reach closed sections and open them. Closed sections stay in the DOM. */
  hiddenUntilFound?: boolean
}

function AccordionInner<Value extends string>(
  {
    label,
    items,
    multiple = false,
    value,
    defaultValue,
    onValueChange,
    headingLevel = 3,
    disabled,
    keepMounted,
    hiddenUntilFound,
    className,
    ...props
  }: AccordionProps<Value>,
  ref: Ref<HTMLDivElement>,
) {
  const Heading = `h${headingLevel}` as const
  // A name only reaches assistive technology on an element with a role, so a labelled accordion is a group.
  const named = Boolean(label || props['aria-labelledby'])
  return (
    <BaseAccordion.Root
      ref={ref}
      role={named ? 'group' : undefined}
      aria-label={label}
      {...props}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(next) => onValueChange?.(next as Value[])}
      multiple={multiple}
      disabled={disabled}
      keepMounted={keepMounted}
      hiddenUntilFound={hiddenUntilFound}
      className={cn('play-accordion', className)}
    >
      {items.map((item) => (
        <BaseAccordion.Item key={item.value} value={item.value} disabled={item.disabled} className="play-accordion__item">
          <BaseAccordion.Header render={<Heading />} className="play-accordion__header">
            <BaseAccordion.Trigger className="play-accordion__trigger">
              <span className="play-accordion__title">{item.title}</span>
              <ChevronDown className="play-accordion__indicator" aria-hidden="true" />
            </BaseAccordion.Trigger>
          </BaseAccordion.Header>
          <BaseAccordion.Panel className="play-accordion__panel">
            <div className="play-accordion__content">{item.content}</div>
          </BaseAccordion.Panel>
        </BaseAccordion.Item>
      ))}
    </BaseAccordion.Root>
  )
}

/** Stacked sections under headings, where opening one closes the others unless `multiple` is set, such as an FAQ. */
export const Accordion = forwardRef(AccordionInner) as <Value extends string>(
  props: AccordionProps<Value> & { ref?: Ref<HTMLDivElement> },
) => ReactElement
